// components/CardRendiciones.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Tooltip,
  TablePagination,
  TableFooter
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Route as RouteIcon,
  Receipt as ReceiptIcon,
  LocalShipping as LocalShippingIcon
} from '@mui/icons-material';
import moment from 'moment';
import CardExpandible from '../CardExpandible';
import { getResumenRendicionesSucursal } from '../../services/rendiciones.service';

const CardRendiciones = ({ 
  sucursal, 
  desde, 
  hasta, 
  loading = false,
  triggerRecarga = 0
}) => {
  // Estados
  const [data, setData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [expandedRows, setExpandedRows] = useState({});

  // Extraer datos de sucursal
  const sucursalId = sucursal?.id;
  const sucursalNombre = sucursal?.nombre;

  // Efecto para cargar datos
  useEffect(() => {
    if (sucursalId && desde && hasta) {
      cargarDatos();
    }
  }, [sucursalId, desde, hasta, triggerRecarga]);

  const cargarDatos = async () => {
    setLoadingData(true);
    setError(null);
    
    try {
      const response = await getResumenRendicionesSucursal(sucursalId, desde, hasta);
      
      if (Array.isArray(response)) {
        setData(response);
        setPage(0);
      } else if (response.success) {
        setData(response.data || []);
        setPage(0);
      } else {
        setError(response.error || 'Error al cargar los datos');
      }
    } catch (error) {
      console.error('Error al cargar rendiciones:', error);
      setError(error.message || 'Error al cargar los datos de rendiciones');
    } finally {
      setLoadingData(false);
    }
  };

  // Manejadores de paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setExpandedRows({});
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedRows({});
  };

  const toggleExpandRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Obtener estado de la rendición
  const getEstadoInfo = (estado) => {
    switch(estado) {
      case 272:
        return { label: 'Confirmada', color: 'success', icon: <CheckCircleIcon fontSize="small" /> };
      case 271:
        return { label: 'Pendiente', color: 'warning', icon: <PendingIcon fontSize="small" /> };
      case 273:
        return { label: 'Anulada', color: 'error', icon: <CancelIcon fontSize="small" /> };
      default:
        return { label: 'Desconocido', color: 'default', icon: null };
    }
  };

  // Calcular estadísticas
  const totalRendiciones = data.length;
  const totalConfirmadas = data.filter(item => item.estado === 272).length;
  const totalPendientes = data.filter(item => item.estado === 271).length;
  const totalAnuladas = data.filter(item => item.estado === 273).length;
  
  // Totales de facturas y remitos
  const totalFacturas = data.reduce((sum, item) => sum + (parseInt(item.total_facturas) || 0), 0);
  const totalFacturasAnuladas = data.reduce((sum, item) => sum + (parseInt(item.facturas_anuladas) || 0), 0);
  const totalEntregasRemito = data.reduce((sum, item) => sum + (parseInt(item.entregas_remito) || 0), 0);

  // Paginación
  const datosPaginados = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  // Loading
  if (loading || loadingData) {
    return (
      <CardExpandible title="Rendiciones" icon={AssignmentIcon}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress size={40} sx={{ color: '#757575' }} />
          <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
            Cargando rendiciones...
          </Typography>
        </Box>
      </CardExpandible>
    );
  }

  // Validaciones
  if (!sucursalId || !desde || !hasta) {
    let mensaje = '';
    if (!sucursalId) mensaje = 'Selecciona una sucursal';
    else if (!desde || !hasta) mensaje = 'Selecciona un rango de fechas';
    
    return (
      <CardExpandible title="Rendiciones" icon={AssignmentIcon}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            {mensaje}
          </Typography>
        </Box>
      </CardExpandible>
    );
  }

  if (error) {
    return (
      <CardExpandible title="Rendiciones" icon={AssignmentIcon}>
        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
      </CardExpandible>
    );
  }

  if (!data || data.length === 0) {
    return (
      <CardExpandible title="Rendiciones" icon={AssignmentIcon}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            No hay rendiciones en este período
          </Typography>
        </Box>
      </CardExpandible>
    );
  }

  return (
    <CardExpandible 
      title="Rendiciones" 
      icon={AssignmentIcon}
      subtitle={sucursalNombre ? `Sucursal: ${sucursalNombre} · ` : ''}
    >
      <Box>
        {/* Estadísticas */}
        {/* <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={2.4}>
            <Box sx={{ 
              textAlign: 'center', 
              // p: 0.5, 
              bgcolor: '#e8f5e9', 
              borderRadius: 1,
              border: '1px solid #c8e6c9'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#1a1a1a', fontSize: '1.1rem' }}>
                {totalRendiciones}
              </Typography>
              <Typography variant="caption" color="textSecondary">Total</Typography>
            </Box>
          </Grid>
          <Grid item xs={2.4}>
            <Box sx={{ 
              textAlign: 'center',
              p: 1,
              bgcolor: '#e8f5e9',
              borderRadius: 1
            }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#2e7d32', fontSize: '1.1rem' }}>
                {totalConfirmadas}
              </Typography>
              <Typography variant="caption" color="textSecondary">Confirmadas</Typography>
            </Box>
          </Grid>
          <Grid item xs={2.4}>
            <Box sx={{ 
              textAlign: 'center',
              p: 1,
              bgcolor: '#fff3e0',
              borderRadius: 1
            }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#ed6c02', fontSize: '1.1rem' }}>
                {totalPendientes}
              </Typography>
              <Typography variant="caption" color="textSecondary">Pendientes</Typography>
            </Box>
          </Grid>
          <Grid item xs={2.4}>
            <Box sx={{ 
              textAlign: 'center',
              p: 1,
              bgcolor: '#fce4ec',
              borderRadius: 1
            }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#d32f2f', fontSize: '1.1rem' }}>
                {totalAnuladas}
              </Typography>
              <Typography variant="caption" color="textSecondary">Anuladas</Typography>
            </Box>
          </Grid>
          <Grid item xs={2.4}>
            <Box sx={{ 
              textAlign: 'center',
              p: 1,
              bgcolor: '#e3f2fd',
              borderRadius: 1
            }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#1976d2', fontSize: '1.1rem' }}>
                {totalFacturas}
              </Typography>
              <Typography variant="caption" color="textSecondary">Facturas</Typography>
            </Box>
          </Grid>
        </Grid> */}

        <Divider sx={{ mb: 2 }} />

        {/* Tabla */}
        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: '#e8e8e8' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>N° Rendición</TableCell>
                <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Chofer</TableCell>
                <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Vehículo</TableCell>
                {/* <TableCell align="center" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Facturas</TableCell>
                <TableCell align="center" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Anuladas</TableCell>
                <TableCell align="center" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Remitos</TableCell> */}
                <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Estado</TableCell>
                <TableCell align="center" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {datosPaginados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                      No hay registros para mostrar
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                datosPaginados.map((item, index) => {
                  const globalIndex = page * rowsPerPage + index;
                  const key = `rendicion-${globalIndex}`;
                  const estadoInfo = getEstadoInfo(item.estado);
                  
                  return (
                    <React.Fragment key={key}>
                      <TableRow hover>
                        <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          {item.nro_rendicion || '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                            {item.chofer || '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                            {item.fecha ? moment(item.fecha).format('DD-MM-YYYY') : '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                            {item.patente || '-'}
                        </TableCell>
                        {/* <TableCell sx={{ fontSize: '0.8rem' }}>
                            {item.total_facturas || '-'}
                        </TableCell> */}
                        {/* <TableCell sx={{ fontSize: '0.8rem' }}>
                            {item.facturas_anuladas || '-'}
                        </TableCell> */}
                         {/* <TableCell sx={{ fontSize: '0.8rem' }}>
                            {item.entregas_remito || '-'}
                        </TableCell> */}
                         <TableCell sx={{ fontSize: '0.8rem' }}>
                            {item.estado || '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ver detalle">
                            <IconButton 
                              size="small" 
                              onClick={() => toggleExpandRow(key)}
                              sx={{ color: '#757575' }}
                            >
                              {expandedRows[key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {/* Fila expandida */}
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 0 }}>
                          <Box sx={{ 
                            display: expandedRows[key] ? 'block' : 'none'
                          }}>
                            <Box sx={{ 
                              p: 1.5, 
                              bgcolor: '#fafafa',
                              borderTop: '1px solid #e8e8e8'
                            }}>
                              <Grid container spacing={1}>
                                <Grid item xs={12}>
                                  <Box sx={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: 0.5
                                  }}>
                                    {/* <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>N° Rendición:</span>
                                      <span>{item.nro_rendicion || '-'}</span>
                                    </Typography> */}
                                    {/* <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Chofer:</span>
                                      <span>{item.chofer || '-'}</span>
                                    </Typography> */}
                                    {/* <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Fecha:</span>
                                      <span>{item.fecha ? moment(item.fecha).format('DD-MM-YYYY') : '-'}</span>
                                    </Typography> */}
                                    {/* <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Vehículo:</span>
                                      <span>{item.patente || '-'}</span>
                                    </Typography> */}
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>KM Inicial:</span>
                                      <span>{item.km_inicial || '-'}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>KM Final:</span>
                                      <span>{item.km_final || '-'}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Total KM:</span>
                                      <span>{parseInt(item.km_final) - parseInt(item.km_inicial) || '-'}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Total Facturas:</span>
                                      <span style={{ fontWeight: 500, color: '#2e7d32' }}>{item.total_facturas || 0}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Facturas Anuladas:</span>
                                      <span style={{ fontWeight: 500, color: '#d32f2f' }}>{item.facturas_anuladas || 0}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Entregas con Remito:</span>
                                      <span style={{ fontWeight: 500, color: '#1976d2' }}>{item.entregas_remito || 0}</span>
                                    </Typography>
                                    {/* <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Estado:</span>
                                      <span>{estadoInfo.label}</span>
                                    </Typography> */}
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={9} />
                </TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  colSpan={9}
                  count={data.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} de ${count}`
                  }
                  sx={{
                    '.MuiTablePagination-select': {
                      fontSize: '0.8rem'
                    },
                    '.MuiTablePagination-displayedRows': {
                      fontSize: '0.8rem'
                    }
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        {/* Información adicional */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="textSecondary">
            Sucursal ID: {sucursalId} · Total Facturas: {totalFacturas} · Anuladas: {totalFacturasAnuladas} · Remitos: {totalEntregasRemito}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Total rendiciones: {data.length} · Actualizado: {moment().format('DD-MM-YYYY HH:mm')}
          </Typography>
        </Box>
      </Box>
    </CardExpandible>
  );
};

export default CardRendiciones;